/**
 * SessionManager — owns the in-memory session lifecycle per
 * /design/specs/4_architecture_c4_l3_components.md §2.2.
 *
 * V1 first slice: config → session conversion + first-hand sampling.
 * V1 4c slice: applyHeroAction wrapping, villain auto-response (simple
 * inline heuristic — TODO: swap to ArchetypeBehaviorDriver once a real spec
 * is loaded from /archetypes/14_fish_tendencies.yaml), street auto-advance,
 * next-hand sampling.
 *
 * Action-feedback slice: applyHeroAction and startNextHand now return a list
 * of intermediate HandState frames in addition to the final session. The UI
 * plays them one at a time so each villain action / street advance gets its
 * own moment to be displayed (announcement, chip stack, fold animation, etc.)
 * instead of the user seeing only the final state.
 *
 * Future: attachGrade, markSessionComplete, attachOutcome history.
 */

import { generateAutoName, type Session, type SessionConfig } from '@/types/session';
import { persistence } from './persistence';
import type { RubricGrade } from '@/types/grade';
import { cryptoSeededRng, type RNG } from '@/lib/rng';
import { sampleHand } from './gameEngine';
import type { Card } from '@/types/card';
import type { PlayerAction } from '@/types/action';
import type { HandState } from '@/types/handState';
import {
  advanceStreet,
  applyAction,
  isTerminal,
  transitionToShowdown,
} from '@/lib/engine/stateMachine';
import { freshDeck, removeCards } from '@/lib/engine/deck';
import { classifyHand } from '@/lib/engine/handClassClassifier';

/** Lightweight UUID generator. */
function newId(): string {
  return crypto.randomUUID();
}

/**
 * Result of a "play forward" call. `session` is the canonical final session
 * (also already persisted to localStorage). `frames` is the ordered list of
 * intermediate HandStates the UI should display, one per "step." The last
 * frame in `frames` always has the same activeHand as `session`.
 */
export interface PlayForwardResult {
  session: Session;
  frames: HandState[];
}

export interface SessionManager {
  startSession(config: SessionConfig): Session;
  loadActiveSession(): Session | null;
  discardActiveSession(): void;
  rememberConfig(config: SessionConfig): void;
  loadLastConfig(): SessionConfig | null;

  /**
   * Apply hero's action. Auto-runs villain's response, auto-advances streets
   * (dealing remaining board cards), handles all-in runouts, transitions to
   * showdown on river. Returns the updated session and the ordered list of
   * intermediate HandState frames so the UI can animate each step.
   *
   * @param decisionElapsedMs Wall-clock milliseconds the hero spent on
   *   this decision (timer start → action confirm). Optional — when
   *   provided, it's attached immutably to the newly-created hero
   *   ActionRecord for downstream time-score grading.
   */
  applyHeroAction(
    action: PlayerAction,
    decisionElapsedMs?: number,
  ): PlayForwardResult;

  /**
   * Sample a fresh hand for the active session. Returns the updated session
   * and the intermediate frames (initial state + any villain pre-actions
   * before hero's first decision).
   */
  startNextHand(): PlayForwardResult;

  /**
   * Attach a transcript to a specific action record (immutable update).
   * Called by ActiveHand once WhisperAdapter finishes transcribing the
   * audio captured during a hero decision. Persists to localStorage.
   *
   * Behavior is fire-and-forget: if the hand has advanced past the
   * targeted action's hand (handId mismatch), or the index is out of
   * range, the call is silently dropped — V1 doesn't surface "transcript
   * couldn't be saved" anywhere user-facing.
   */
  attachTranscript(handId: string, actionIndex: number, text: string): void;

  /**
   * Attach a grade to the active hand. Called by PostHandGrade once
   * GradeService returns a graded outcome. Persists to localStorage so
   * a page reload restores the same grade without re-paying for it.
   * No-ops if the active hand has advanced past the supplied handId.
   */
  attachGrade(handId: string, grade: RubricGrade): void;
}

class V1SessionManager implements SessionManager {
  startSession(config: SessionConfig): Session {
    const now = new Date();
    const rng = cryptoSeededRng();
    const sampled = sampleHand(config, rng);
    // Progress to hero's decision (e.g., run villain's auto-action if villain
    // is OOP and acts first postflop). For the very-first hand of a session
    // we don't animate the initial deal — the user lands on hero's decision
    // directly. (Subsequent hands DO animate via startNextHand.)
    const frames = collectFramesToHeroOrTerminal(sampled.state, rng);
    const initialState = frames[frames.length - 1];

    const session: Session = {
      id: newId(),
      config,
      autoName: generateAutoName(config, now),
      startedAt: now.toISOString(),
      activeHand: initialState,
      activeHandResolved: sampled.resolved,
    };
    persistence.saveActiveSession(session);
    persistence.saveLastConfig(config);
    return session;
  }

  loadActiveSession(): Session | null {
    return persistence.loadActiveSession();
  }

  discardActiveSession(): void {
    persistence.clearActiveSession();
  }

  rememberConfig(config: SessionConfig): void {
    persistence.saveLastConfig(config);
  }

  loadLastConfig(): SessionConfig | null {
    return persistence.loadLastConfig();
  }

  applyHeroAction(
    action: PlayerAction,
    decisionElapsedMs?: number,
  ): PlayForwardResult {
    const session = this.loadActiveSession();
    if (!session || !session.activeHand) {
      throw new Error('applyHeroAction: no active session/hand');
    }
    const rng = cryptoSeededRng();
    let stateAfterHero = applyAction(session.activeHand, action);
    // Stamp the decision-elapsed time onto the new hero record (the last
    // entry in history). Done as an immutable update so downstream
    // consumers don't see a partially-mutated state.
    if (decisionElapsedMs !== undefined && stateAfterHero.history.length > 0) {
      const lastIdx = stateAfterHero.history.length - 1;
      stateAfterHero = {
        ...stateAfterHero,
        history: stateAfterHero.history.map((rec, i) =>
          i === lastIdx ? { ...rec, decisionElapsedMs } : rec,
        ),
      };
    }
    const frames = collectFramesToHeroOrTerminal(stateAfterHero, rng);
    const finalState = frames[frames.length - 1];

    const updated: Session = { ...session, activeHand: finalState };
    persistence.saveActiveSession(updated);
    return { session: updated, frames };
  }

  startNextHand(): PlayForwardResult {
    const session = this.loadActiveSession();
    if (!session) throw new Error('startNextHand: no active session');
    const rng = cryptoSeededRng();
    const sampled = sampleHand(session.config, rng);
    const frames = collectFramesToHeroOrTerminal(sampled.state, rng);
    const finalState = frames[frames.length - 1];

    const updated: Session = {
      ...session,
      activeHand: finalState,
      activeHandResolved: sampled.resolved,
      // Explicitly clear the previous hand's grade so PostHandGrade for the
      // new hand doesn't render the old grade while waiting on the new one.
      // The handId tag is also a guard, but clearing is the cleaner intent.
      activeHandGrade: null,
    };
    persistence.saveActiveSession(updated);
    return { session: updated, frames };
  }

  attachTranscript(handId: string, actionIndex: number, text: string): void {
    const session = this.loadActiveSession();
    if (!session || !session.activeHand) return;
    if (session.activeHand.handId !== handId) return;
    const history = session.activeHand.history;
    if (actionIndex < 0 || actionIndex >= history.length) return;
    // Immutable update — replace the targeted record with one carrying
    // the transcript, leave others untouched.
    const newHistory = history.map((rec, i) =>
      i === actionIndex ? { ...rec, transcript: text } : rec,
    );
    const updated: Session = {
      ...session,
      activeHand: { ...session.activeHand, history: newHistory },
    };
    persistence.saveActiveSession(updated);
  }

  attachGrade(handId: string, grade: RubricGrade): void {
    const session = this.loadActiveSession();
    if (!session || !session.activeHand) return;
    if (session.activeHand.handId !== handId) return;
    const updated: Session = {
      ...session,
      activeHandGrade: { handId, grade },
    };
    persistence.saveActiveSession(updated);
  }
}

/** Single shared instance used across the app. */
export const sessionManager: SessionManager = new V1SessionManager();

// =============================================================================
// Hand-progression helpers
// =============================================================================

/**
 * Run the engine forward until hero's decision is needed, or the hand is
 * terminal. Returns ALL intermediate states (including the input state at
 * index 0 and the final state at the last index). Handles villain
 * auto-action, between-street card dealing, and all-in runout completion.
 *
 * Caller invariant: every frame except the last has either:
 *   - phase === 'street_closed' (about to deal next card), or
 *   - phase === 'street_open' && toAct === 'villain' (villain just acted
 *     and… wait, no — we'd have continued. See note below.)
 *
 * The final frame is always hero's-turn-open or terminated.
 */
function collectFramesToHeroOrTerminal(start: HandState, rng: RNG): HandState[] {
  const frames: HandState[] = [start];
  let state = start;
  for (let safety = 0; safety < 50; safety++) {
    if (isTerminal(state)) return frames;
    if (state.phase === 'street_open' && state.toAct === 'hero') return frames;

    if (state.phase === 'street_open' && state.toAct === 'villain') {
      const villainAction = decideFishActionSimple(state);
      state = applyAction(state, villainAction);
      frames.push(state);
      continue;
    }

    if (state.phase === 'street_closed') {
      if (state.street === 'river') {
        state = transitionToShowdown(state);
        frames.push(state);
        continue;
      }
      const card = dealNextCard(state, rng);
      state = advanceStreet(state, [card]);
      frames.push(state);
      continue;
    }
  }
  throw new Error('collectFramesToHeroOrTerminal: safety limit exceeded');
}

/**
 * Deal a single card from the deck minus all cards already in play (hole cards
 * + current board). Used for advancing to the next street.
 */
function dealNextCard(state: HandState, rng: RNG): Card {
  const dead: Card[] = [
    ...state.heroCards,
    ...state.villainCards,
    ...state.board,
  ];
  const live = removeCards(freshDeck(), dead);
  if (live.length === 0) throw new Error('dealNextCard: deck exhausted');
  return rng.pick(live);
}

/**
 * Simple Fish heuristic — placeholder for the full ArchetypeBehaviorDriver
 * with a YAML-loaded spec. Behavior:
 *
 *   - No bet to face: check
 *   - Facing a bet:
 *       cpfs / thick_value → call
 *       thin_value vs ≤medium → call, else fold
 *       combo_draw / he_draw vs ≤medium → call, else fold
 *       le_draw vs small → call, else fold
 *       sdv vs small → call, else fold
 *       air → fold
 *
 *  TODO(v1-yaml-loader): replace with decideVillainAction(state, fishSpec, rng).
 */
function decideFishActionSimple(state: HandState): PlayerAction {
  if (state.toCall === 0) return { kind: 'check' };

  const handClass = classifyHand(state.villainCards, state.board);
  const potBeforeFacingBet = state.pot - state.toCall;
  const ratio = state.toCall / Math.max(1, potBeforeFacingBet);

  const isSmall = ratio < 0.4;
  const isMedium = ratio < 0.9;

  switch (handClass) {
    case 'cpfs':
    case 'thick_value':
      return { kind: 'call' };
    case 'thin_value':
      return isMedium ? { kind: 'call' } : { kind: 'fold' };
    case 'combo_draw':
    case 'he_draw':
      return isMedium ? { kind: 'call' } : { kind: 'fold' };
    case 'le_draw':
      return isSmall ? { kind: 'call' } : { kind: 'fold' };
    case 'sdv':
      return isSmall ? { kind: 'call' } : { kind: 'fold' };
    case 'air':
      return { kind: 'fold' };
  }
}
